import React, { useState, useEffect, useRef } from 'react';
import {
  styled,
  useTheme,
  Drawer,
  Divider,
  List,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import {
  ExpandLess,
  ExpandMore,
  Home as HomeIcon,
  Info as InfoIcon,
  Menu as MenuIcon,
  Chat as ChatIcon,
  Description as DescriptionIcon,
  Lock as LockIcon,
  PersonAdd as PersonAddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

import { Colors } from '../style/theme';
import { isLoggedIn } from 'src/authorization/authUtils';

const drawerWidth = 240;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 5),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

const DrawerItem = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const drawerRef = useRef(null);


  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);
  const scrollToSection = (sectionId) => {
    if (window.location.pathname !== '/') {
      navigate(`/#${sectionId}`);
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };
  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const handleItemClick = (index) => {
    setActiveItem(index);
    setOpen(false);
    if (index === 1) {
      scrollToSection('pricing');
    } else if (index === 0) {
      scrollToSection('services');
    } else {
      navigate('/#pricing');
    }
  };

  const toggleResources = () => setResourcesOpen(!resourcesOpen);

  const itemList = [
    { text: 'Services', icon: <ChatIcon />, to: '/#services' },
    { text: 'Pricing', icon: <HomeIcon />, to: '/#pricing' },
    { text: 'Contact Us', icon: <InfoIcon />, to: '/contact' },
    { text: 'Resources', icon: <DescriptionIcon />, isDropdown: true },
    { text: 'LogIn', icon: <LockIcon />, to: isLoggedIn() ? '/dashboard' : '/login' },
    { text: 'SignUp', icon: <PersonAddIcon />, to: isLoggedIn() ? '/dashboard' : '/signup' },
  ];

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="end"
        onClick={handleDrawerOpen}
        sx={{ ...(open && { display: 'none' }) }}
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        ref={drawerRef}
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
          },
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose} aria-label="Close navigation menu">
            {theme.direction === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {itemList.map((item, index) => {
            const { text, to, icon, isDropdown } = item;
            const isActive = activeItem === index;

            if (isDropdown) {
              return (
                <React.Fragment key={text}>
                  <ListItem
                    button
                    onClick={toggleResources}
                    sx={{
                      color: Colors.dim_grey,
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: Colors.dark_shaft,
                        color: Colors.white,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: Colors.grey }}>{icon}</ListItemIcon>
                    <ListItemText primary={text} />
                    {resourcesOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={resourcesOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {[
                        { label: 'Blog', path: '/blog' },
                        { label: 'Documentation', path: '/document' },
                        { label: 'Case Studies', path: '/case-studies' },
                        { label: 'FAQs', path: '/faq' },
                        { label: 'About Us', path: '/about' },
                      ].map((subItem) => (
                        <ListItem
                          component={Link}
                          to={subItem.path}
                          key={subItem.label}
                          onClick={() => {
                            setOpen(false);
                            setResourcesOpen(false);
                          }}
                          sx={{
                            pl: 4,
                            color: Colors.dim_grey,
                            '&:hover': {
                              backgroundColor: Colors.shaft,
                              color: Colors.white,
                            },
                          }}
                        >
                          <ListItemText primary={subItem.label} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            return (
              <ListItem
                key={text}
                component={Link}
                to={to}
                onClick={() => handleItemClick(index)}
                sx={{
                  color: isActive ? Colors.white : Colors.dim_grey,
                  backgroundColor: isActive ? Colors.shaft : 'transparent',
                  '&:hover': {
                    backgroundColor: Colors.dark_shaft,
                    color: Colors.white,
                  },
                }}
              >
                <ListItemIcon sx={{ color: isActive ? Colors.white : Colors.grey }}>
                  {icon}
                </ListItemIcon>
                <ListItemText primary={text} />
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </>
  );
};

export default DrawerItem;
